import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const MAX_MESSAGE = 5000;

export default function SupportPanel() {
  const theme = useTheme();
  const { isLoggedIn, userId } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const resetForm = useCallback(() => {
    setMessage('');
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!isLoggedIn || !userId) {
      setName('');
      setEmail('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/showprofile/${userId}`
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const fn = (data.firstName || '').trim();
        const ln = (data.lastName || '').trim();
        const displayName = [fn, ln].filter(Boolean).join(' ') || data.username || '';
        setName(displayName);
        setEmail(data.email || '');
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isLoggedIn, userId]);

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      setSnackbar({
        open: true,
        message: 'Fyll i alla fält.',
        severity: 'warning',
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/support-contact`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: trimmedName,
            email: trimmedEmail,
            message: trimmedMessage,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Kunde inte skicka meddelandet.');
      }
      setSnackbar({
        open: true,
        message: 'Tack! Ditt meddelande har skickats.',
        severity: 'success',
      });
      setMessage('');
      handleClose();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Något gick fel. Försök igen.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        className="no-print"
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 1200,
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          p: 0,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow: theme.shadows[4],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            bgcolor: 'primary.dark',
            transform: 'scale(1.05)',
            boxShadow: theme.shadows[8],
          },
        }}
        aria-label="Kontakta support"
      >
        <Box
          sx={{
            position: 'relative',
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'primary.contrastText',
              opacity: 0.95,
            }}
          />
          <Typography
            component="span"
            sx={{
              position: 'relative',
              fontSize: '1.1rem',
              fontWeight: 700,
              lineHeight: 1,
              userSelect: 'none',
            }}
          >
            ?
          </Typography>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100vw',
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 2.5,
            pt: 1.5,
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 1 }}>
              <MailOutlineIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Kontakta support
              </Typography>
            </Stack>
            <IconButton
              onClick={handleClose}
              aria-label="Stäng"
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Har du en fråga om funktionerna eller hur du använder Seglingssajten? Skicka ett meddelande till vår support så hjälper vi
            dig.
          </Typography>

          <TextField
            label="Namn"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            margin="normal"
            placeholder="Ditt namn"
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 0 }}
          />
          <TextField
            label="E-post"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            margin="normal"
            placeholder="din@epost.se"
            InputLabelProps={{ shrink: true }}
            autoComplete="email"
          />
          <TextField
            label="Meddelande"
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
            required
            fullWidth
            margin="normal"
            multiline
            minRows={6}
            placeholder="Beskriv ditt problem eller din fråga..."
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: MAX_MESSAGE }}
            sx={{
              '& .MuiInputBase-inputMultiline': {
                resize: 'vertical',
                overflow: 'auto',
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-end', mt: 0.5 }}>
            {message.length}/{MAX_MESSAGE} tecken
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ mt: 'auto', pt: 3 }}>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              onClick={handleClose}
              disabled={submitting}
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={22} color="inherit" /> : 'Skicka'}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2.5, lineHeight: 1.5 }}>
            Läs vår{' '}
            <Link component={RouterLink} to="/privacy" underline="hover" color="primary">
              integritetspolicy
            </Link>{' '}
            och våra{' '}
            <Link component={RouterLink} to="/terms" underline="hover" color="primary">
              användarvillkor
            </Link>
            .
          </Typography>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
